import { type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import Button from "common/core/button";
import { useForm } from "common/core/form";
import { pushNotification } from "common/core/notification_center/actions";
import WorkflowModal from "common/modals/workflow_modal";
import type { ENoteOrganizationTransaction_document_bundle_documents_edges_node as Document } from "common/transaction_creation/v3/validations/enote/organization_transaction_fragment.graphql";
import { NOTIFICATION_TYPES } from "constants/notifications";
import { captureException } from "util/exception";
import { useMutation } from "util/graphql";
import { isGraphQLError } from "util/graphql/query";
import { userFullName } from "util/user";

import { DeprecatedSignerSsnField } from "./signer_ssn";
import UpdateMortgageBorrowerMutation from "./update_mortgage_borrower_mutation.graphql";

const SSN_FIELD_PREFIX = "borrower-ssn-";

type Props = {
  onCancel: () => void;
  document: Document;
  onSuccess?: () => void;
  headingText?: string | ReactNode;
  skipButtonText?: string | ReactNode;
};

export type SignerSSNFormValues = Record<string, string | undefined>;

export function AssignSsnModal(props: Props) {
  const form = useForm<SignerSSNFormValues>();

  const {
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = form;

  const updateMortgageBorrower = useMutation(UpdateMortgageBorrowerMutation);

  const { document, headingText, skipButtonText, onCancel, onSuccess } = props;

  const headerText = headingText || (
    <FormattedMessage
      id="4a568aa5-3cd7-4e97-b96f-2b2cd50f4ada"
      defaultMessage="Assign a social security number"
    />
  );

  const skipText = skipButtonText || (
    <FormattedMessage id="30961b6a-7ace-4893-a9cb-d0e9e2e675f5" defaultMessage="Cancel" />
  );

  async function updateMortgageBorrowers(data: SignerSSNFormValues) {
    try {
      const promises = Object.entries(data).map(([key, ssn]) => {
        const index = key.replace(SSN_FIELD_PREFIX, "");
        return updateMortgageBorrower({
          variables: {
            input: {
              id: props.document.mortgageBorrowers.edges[Number(index)].node.id,
              ssn: ssn?.replace(/[-\s]/g, ""),
            },
          },
        });
      });
      await Promise.all(promises);
      onSuccess?.();
      pushNotification({
        type: NOTIFICATION_TYPES.DEFAULT,
        subtype: "SUCCESS",
        message: (
          <FormattedMessage
            id="1f1ce94d-71ca-430e-83d7-485d99f3689a"
            defaultMessage="SSN saved successfully."
          />
        ),
        position: "topCenter",
      });
    } catch (error) {
      if (isGraphQLError(error)) {
        pushNotification({
          type: NOTIFICATION_TYPES.DEFAULT,
          subtype: "ERROR",
          message: (
            <FormattedMessage
              id="cdfdf7d5-0ff9-4ce0-ba55-c927895fc334"
              defaultMessage="An error occurred while saving the SSN."
            />
          ),
          position: "topCenter",
        });
      } else {
        captureException(error);
      }
    }
  }

  const buttons = [
    <Button
      key="assign-ssn"
      automationId="assign-ssn"
      disabled={isSubmitting}
      isLoading={isSubmitting}
      onClick={handleSubmit(updateMortgageBorrowers)}
      buttonColor="action"
      variant="primary"
    >
      <FormattedMessage id="bbd77d22-589b-4ba9-8023-d86b15b31223" defaultMessage="Assign SSN" />
    </Button>,
    <Button
      key="cancel"
      automationId="cancel-button"
      onClick={() => {
        reset();
        onCancel();
      }}
      disabled={isSubmitting}
      buttonColor="action"
      variant="secondary"
    >
      {skipText}
    </Button>,
  ];

  return (
    <WorkflowModal title={headerText} footerSeparator={false} buttons={buttons}>
      <form className="AssignSsn--form" autoComplete="off">
        {document.mortgageBorrowers.edges.map(({ node: mortgageBorrower }, index) => {
          return (
            <DeprecatedSignerSsnField
              key={`mortgageBorrower-${index}`}
              fieldName={`${SSN_FIELD_PREFIX}${index}`}
              fieldLabel={userFullName(mortgageBorrower)}
              hasSsn={mortgageBorrower.hasSsn}
              {...form}
            />
          );
        })}
      </form>
    </WorkflowModal>
  );
}
